import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PostsAPI } from '@/services/postsAPI';
import { Post, PostFormData } from '@/types/posts';
import Page from '@/components/Page';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import LoadingState from '@/components/LoadingState';
import ROUTES from '@/routes/routes';

const PostForm: React.FC = () => {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const isEdit = !!postId;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [formData, setFormData] = useState<PostFormData>({
    title_en: '',
    title_es: '',
    excerpt_en: '',
    excerpt_es: '',
    link: '',
    image_url: '',
    thumbnail_url: '',
    category: 'whats_new',
    status: 'draft',
    display_order: 0,
    published_at: '',
    expires_at: '',
  });

  useEffect(() => {
    if (isEdit && postId) {
      fetchPost();
    }
  }, [isEdit, postId]);

  const fetchPost = async () => {
    if (!postId) return;

    setInitialLoading(true);
    setError(null);
    try {
      const response = await PostsAPI.getPost(Number(postId));
      if (response.success && response.data) {
        const post = response.data;
        setFormData({
          title_en: post.title_en || '',
          title_es: post.title_es || '',
          excerpt_en: post.excerpt_en || '',
          excerpt_es: post.excerpt_es || '',
          link: post.link || '',
          image_url: post.image_url || '',
          thumbnail_url: post.thumbnail_url || '',
          category: post.category || 'whats_new',
          status: post.status || 'draft',
          display_order: post.display_order || 0,
          published_at: post.published_at
            ? new Date(post.published_at).toISOString().slice(0, 16)
            : '',
          expires_at: post.expires_at
            ? new Date(post.expires_at).toISOString().slice(0, 16)
            : '',
        });
      }
    } catch (err: any) {
      console.error('Error fetching post:', err);
      setError(err.response?.data?.errors?.join(', ') || 'Failed to load post');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setError(null);

    try {
      // Get presigned URL
      const uploadData = await PostsAPI.getUploadUrl(file.name, file.type);

      // Upload to S3
      await fetch(uploadData.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Update form data with public URL
      setFormData((prev) => ({ ...prev, image_url: uploadData.public_url }));
    } catch (err: any) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleThumbnailUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingThumbnail(true);
    setError(null);

    try {
      // Get presigned URL
      const uploadData = await PostsAPI.getUploadUrl(file.name, file.type);

      // Upload to S3
      await fetch(uploadData.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Update form data with public URL
      setFormData((prev) => ({
        ...prev,
        thumbnail_url: uploadData.public_url,
      }));
    } catch (err: any) {
      console.error('Error uploading thumbnail:', err);
      setError('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = { ...formData };

      // Convert empty strings to undefined
      if (!submitData.image_url) submitData.image_url = undefined;
      if (!submitData.thumbnail_url) submitData.thumbnail_url = undefined;
      if (!submitData.published_at) submitData.published_at = undefined;
      if (!submitData.expires_at) submitData.expires_at = undefined;

      if (isEdit && postId) {
        await PostsAPI.updatePost(Number(postId), submitData);
      } else {
        await PostsAPI.createPost(submitData);
      }

      navigate(`/${ROUTES.posts}`);
    } catch (err: any) {
      console.error('Error saving post:', err);
      console.error('Error response:', err.response);
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.errors?.join(', ') ||
        err.response?.data?.message ||
        err.message ||
        'Failed to save post';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${ROUTES.posts}`);
  };

  if (initialLoading) {
    return (
      <Page title={isEdit ? 'Edit Post' : 'Create Post'}>
        <Card>
          <LoadingState />
        </Card>
      </Page>
    );
  }

  return (
    <Page
      backAction={{
        label: 'Back to Posts',
        onAction: handleCancel,
      }}
      size="md"
      title={isEdit ? 'Edit Post' : 'Create New Post'}
    >
      <form onSubmit={handleSubmit}>
        <Card>
          {error && (
            <Card.Section>
              <Alert description={error} status="danger" title="Error" />
            </Card.Section>
          )}

          <Card.Section>
            <Stack gap={16} vertical>
              <div style={{ width: '100%' }}>
                <label
                  htmlFor="title_en"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Title (English) *</Body>
                </label>
                <input
                  id="title_en"
                  name="title_en"
                  required
                  type="text"
                  value={formData.title_en}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ width: '100%' }}>
                <label
                  htmlFor="title_es"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Title (Spanish) *</Body>
                </label>
                <input
                  id="title_es"
                  name="title_es"
                  required
                  type="text"
                  value={formData.title_es}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ width: '100%' }}>
                <label
                  htmlFor="excerpt_en"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Excerpt (English) *</Body>
                </label>
                <textarea
                  id="excerpt_en"
                  name="excerpt_en"
                  required
                  rows={3}
                  value={formData.excerpt_en}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ width: '100%' }}>
                <label
                  htmlFor="excerpt_es"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Excerpt (Spanish) *</Body>
                </label>
                <textarea
                  id="excerpt_es"
                  name="excerpt_es"
                  required
                  rows={3}
                  value={formData.excerpt_es}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ width: '100%' }}>
                <label
                  htmlFor="link"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Link URL *</Body>
                </label>
                <input
                  id="link"
                  name="link"
                  required
                  type="url"
                  value={formData.link}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ width: '100%' }}>
                <label
                  htmlFor="image_file"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Image</Body>
                </label>
                <input
                  id="image_file"
                  name="image_file"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
                {uploadingImage && <Small>Uploading...</Small>}
                {formData.image_url && (
                  <div style={{ marginTop: '8px' }}>
                    <Small>Current image:</Small>
                    <br />
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        marginTop: '4px',
                      }}
                    />
                  </div>
                )}
              </div>

              <div style={{ width: '100%' }}>
                <label
                  htmlFor="thumbnail_file"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  <Body weight="medium">Thumbnail</Body>
                </label>
                <input
                  id="thumbnail_file"
                  name="thumbnail_file"
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={uploadingThumbnail}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  }}
                />
                {uploadingThumbnail && <Small>Uploading...</Small>}
                {formData.thumbnail_url && (
                  <div style={{ marginTop: '8px' }}>
                    <Small>Current thumbnail:</Small>
                    <br />
                    <img
                      src={formData.thumbnail_url}
                      alt="Thumbnail preview"
                      style={{
                        maxWidth: '100px',
                        maxHeight: '100px',
                        marginTop: '4px',
                      }}
                    />
                  </div>
                )}
              </div>

              <Stack gap={12}>
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="category"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Category *</Body>
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <option value="whats_new">What's New</option>
                    <option value="know_the_basics">Know the Basics</option>
                    <option value="announcements">Announcements</option>
                    <option value="tips_and_tricks">Tips and Tricks</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="status"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Status *</Body>
                  </label>
                  <select
                    id="status"
                    name="status"
                    required
                    value={formData.status}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="display_order"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Display Order *</Body>
                  </label>
                  <input
                    id="display_order"
                    name="display_order"
                    required
                    type="number"
                    value={formData.display_order}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </Stack>

              <Stack gap={12}>
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="published_at"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Published At</Body>
                  </label>
                  <input
                    id="published_at"
                    name="published_at"
                    type="datetime-local"
                    value={formData.published_at}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="expires_at"
                    style={{ display: 'block', marginBottom: '4px' }}
                  >
                    <Body weight="medium">Expires At</Body>
                  </label>
                  <input
                    id="expires_at"
                    name="expires_at"
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </Stack>
            </Stack>
          </Card.Section>

          <Card.Section subdued>
            <Stack gap={12} justify="end">
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                type="button"
                onAction={handleCancel}
              />
              <Button
                a11yLabel={isEdit ? 'Update Post' : 'Create Post'}
                isLoading={loading}
                label={isEdit ? 'Update' : 'Create'}
                type="submit"
              />
            </Stack>
          </Card.Section>
        </Card>
      </form>
    </Page>
  );
};

export default PostForm;
